import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Add, DeleteOutline, Edit, GetApp, PlaylistAddCheck, Translate } from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import ForbiddenPage from "../../components/ForbiddenPage";
import AudioModal from "../../components/AudioModal";
import ModalImageCors from "../../components/ModalImageCors";
import { BACKEND_URL } from "../../config/env";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useDebounce } from "use-debounce";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    ...theme.scrollbarStyles
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2)
  },
  cardsGrid: {
    marginBottom: theme.spacing(2)
  },
  statCard: {
    padding: theme.spacing(2),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    height: "100%"
  },
  statLabel: {
    fontSize: "0.8rem",
    opacity: 0.8,
    marginBottom: theme.spacing(0.5)
  },
  statValue: {
    fontSize: "1.6rem",
    fontWeight: 700
  },
  messageBubble: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    padding: theme.spacing(1.25),
    marginBottom: theme.spacing(1),
    background: theme.palette.background.default
  },
  messageMeta: {
    fontSize: "0.75rem",
    opacity: 0.75,
    marginBottom: theme.spacing(0.5)
  },
  formGrid: {
    marginTop: theme.spacing(1)
  },
  quickReviewGrid: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1)
  },
  scoreField: {
    minWidth: 120
  },
  statusChip: {
    fontWeight: 700
  },
  summaryBox: {
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    marginBottom: theme.spacing(1.5),
    whiteSpace: "pre-wrap"
  },
  compactActions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginBottom: theme.spacing(1.5)
  },
  compactStatusBtn: {
    minWidth: 130
  },
  suggestionCard: {
    padding: theme.spacing(1.5),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(1),
    background: theme.palette.background.default
  },
  suggestionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  helperText: {
    opacity: 0.75,
    marginBottom: theme.spacing(1.5)
  },
  messageActions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    alignItems: "center",
    marginTop: theme.spacing(1)
  },
  inlineMediaBlock: {
    marginTop: theme.spacing(1)
  },
  participantName: {
    display: "inline-block",
    marginTop: theme.spacing(0.5),
    fontWeight: 700,
    color: theme.palette.primary.main
  },
  insightBox: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: 8,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"
  }
}));

const defaultReview = {
  title: "",
  status: "pending",
  overallScore: "",
  intentScore: "",
  contextScore: "",
  toneScore: "",
  actionScore: "",
  notes: "",
  expectedBehavior: "",
  actualResponseExcerpt: ""
};

const defaultTestCase = {
  name: "",
  description: "",
  category: "general",
  inputMessage: "",
  priorContext: "",
  expectedIntent: "",
  expectedAction: "",
  expectedResponse: "",
  status: "active"
};

const statusColor = (status) => {
  if (status === "approved") return "primary";
  if (status === "partial") return "secondary";
  if (status === "rejected") return "default";
  return "default";
};

const formatDateTime = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString("es-CO", {
      dateStyle: "short",
      timeStyle: "short"
    });
  } catch (_error) {
    return value;
  }
};

const parseTranscriptionPayload = (value) => {
  if (!value) return null;
  try {
    return typeof value === "string" ? JSON.parse(value) : value;
  } catch (_error) {
    return null;
  }
};

const buildMediaUrl = (mediaUrl, companyId) => {
  const raw = String(mediaUrl || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;

  const base = String(BACKEND_URL || "").replace(/\/$/, "");
  if (!base) return raw;

  return `${base}/public/company${companyId}/${raw.replace(/^\/+/, "")}`;
};

const getMediaFileName = (mediaUrl) => {
  const raw = String(mediaUrl || "").trim();
  if (!raw) return "";

  try {
    if (/^https?:\/\//i.test(raw)) {
      const parsed = new URL(raw);
      return parsed.pathname.split("/").pop() || "";
    }
  } catch (_error) {
    // fallback below
  }

  return raw.split("/").pop() || "";
};

const AiCenter = () => {
  const classes = useStyles();
  const [tab, setTab] = useState("reviews");
  const [overview, setOverview] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [testCases, setTestCases] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingTestCases, setLoadingTestCases] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [debouncedSearch] = useDebounce(searchParam, 400);

  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [conversationDetail, setConversationDetail] = useState(null);
  const [reviewForm, setReviewForm] = useState(defaultReview);
  const [idealSuggestions, setIdealSuggestions] = useState([]);
  const [loadingIdealSuggestions, setLoadingIdealSuggestions] = useState(false);
  const [messageActionLoading, setMessageActionLoading] = useState({});

  const [testCaseModalOpen, setTestCaseModalOpen] = useState(false);
  const [selectedTestCase, setSelectedTestCase] = useState(null);
  const [testCaseForm, setTestCaseForm] = useState(defaultTestCase);
  const [runResult, setRunResult] = useState(null);

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch (_error) {
      return {};
    }
  }, []);

  const updateConversationMessageTranscription = (messageId, payload) => {
    setConversationDetail((prev) => {
      if (!prev?.messages?.length) return prev;

      return {
        ...prev,
        messages: prev.messages.map((message) => {
          if (message.id !== messageId) return message;

          const existing = parseTranscriptionPayload(message.transcription) || {};
          return {
            ...message,
            transcription: JSON.stringify({
              ...existing,
              ...payload
            })
          };
        })
      };
    });
  };

  const handleAiCenterAudioTranscription = async (message) => {
    const fileName = getMediaFileName(message.mediaUrl);
    if (!fileName) return;

    setMessageActionLoading((prev) => ({ ...prev, [`audio-${message.id}`]: true }));
    try {
      const { data } = await api.get(`/messages/transcribeAudio/${encodeURIComponent(fileName)}`);
      updateConversationMessageTranscription(message.id, {
        transcribedText: data.transcribedText || null,
        translatedText: data.translatedText || null,
        detectedLanguage: data.detectedLanguage || null
      });
    } catch (error) {
      toastError(error);
    } finally {
      setMessageActionLoading((prev) => ({ ...prev, [`audio-${message.id}`]: false }));
    }
  };

  const handleAiCenterImageOcr = async (message) => {
    const fileName = getMediaFileName(message.mediaUrl);
    if (!fileName) return;

    setMessageActionLoading((prev) => ({ ...prev, [`image-${message.id}`]: true }));
    try {
      const { data } = await api.get(`/messages/extractImageText/${encodeURIComponent(fileName)}`);
      updateConversationMessageTranscription(message.id, {
        extractedText: data.extractedText || null,
        summaryText: data.summaryText || null,
        detectedLanguage: data.detectedLanguage || null
      });
    } catch (error) {
      toastError(error);
    } finally {
      setMessageActionLoading((prev) => ({ ...prev, [`image-${message.id}`]: false }));
    }
  };

  const renderConversationMessageExtra = (message) => {
    const transcription = parseTranscriptionPayload(message.transcription);
    const extractedText = transcription?.translatedText
      || transcription?.transcribedText
      || transcription?.extractedText;
    const mediaHref = buildMediaUrl(
      message.mediaUrl,
      conversationDetail?.companyId || user.companyId
    );
    const inboundLabel = conversationDetail?.contact?.name || "Cliente";
    const outboundLabel = conversationDetail?.user?.name || "Operador / IA";
    const loadingAudio = Boolean(messageActionLoading[`audio-${message.id}`]);
    const loadingImage = Boolean(messageActionLoading[`image-${message.id}`]);

    return (
      <>
        <Typography variant="caption" display="block" color="textSecondary">
          {message.fromMe ? outboundLabel : inboundLabel}
        </Typography>
        {!message.fromMe ? (
          <Typography variant="body2" className={classes.participantName}>
            {inboundLabel}
          </Typography>
        ) : null}
        {message.mediaType === "audio" && mediaHref ? (
          <Box mt={1} className={classes.inlineMediaBlock}>
            <AudioModal url={mediaHref} />
          </Box>
        ) : null}
        {message.mediaType === "image" && mediaHref ? (
          <Box mt={1} mb={1} className={classes.inlineMediaBlock}>
            <ModalImageCors imageUrl={mediaHref} />
          </Box>
        ) : null}

        {(message.mediaType === "audio" || message.mediaType === "image") && mediaHref ? (
          <div className={classes.messageActions}>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              href={mediaHref}
              target="_blank"
              startIcon={<GetApp />}
            >
              {message.mediaType === "audio" ? "Descargar audio" : "Descargar imagen"}
            </Button>

            {message.mediaType === "image" ? (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                href={mediaHref}
                target="_blank"
              >
                Ver imagen
              </Button>
            ) : null}

            {message.mediaType === "audio" ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleAiCenterAudioTranscription(message)}
                startIcon={loadingAudio ? <CircularProgress size={14} /> : <Translate />}
                disabled={loadingAudio}
              >
                {loadingAudio ? "Transcribiendo..." : "Transcribir"}
              </Button>
            ) : null}

            {message.mediaType === "image" ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleAiCenterImageOcr(message)}
                startIcon={loadingImage ? <CircularProgress size={14} /> : <Translate />}
                disabled={loadingImage}
              >
                {loadingImage ? "Leyendo imagen..." : "OCR imagen"}
              </Button>
            ) : null}
          </div>
        ) : null}

        {message.mediaType === "audio" && extractedText ? (
          <Box className={classes.insightBox}>
            <Typography variant="caption" display="block" color="textSecondary">
              Transcripción de audio
            </Typography>
            <Typography variant="body2">{extractedText}</Typography>
          </Box>
        ) : null}

        {message.mediaType === "image" && extractedText ? (
          <Box className={classes.insightBox}>
            <Typography variant="caption" display="block" color="textSecondary">
              Texto detectado en la imagen
            </Typography>
            <Typography variant="body2">{extractedText}</Typography>
            {transcription?.summaryText ? (
              <Typography variant="body2" style={{ marginTop: 8 }}>
                {transcription.summaryText}
              </Typography>
            ) : null}
          </Box>
        ) : null}
      </>
    );
  };

  const loadOverview = async () => {
    try {
      const { data } = await api.get("/ai-center/overview");
      setOverview(data);
    } catch (error) {
      toastError(error);
    }
  };

  const loadConversations = async () => {
    setLoadingConversations(true);
    try {
      const { data } = await api.get("/ai-center/conversations", {
        params: { searchParam: debouncedSearch }
      });
      setConversations(data.conversations || []);
    } catch (error) {
      toastError(error);
    } finally {
      setLoadingConversations(false);
    }
  };

  const loadTestCases = async () => {
    setLoadingTestCases(true);
    try {
      const { data } = await api.get("/ai-center/test-cases", {
        params: { searchParam: debouncedSearch }
      });
      setTestCases(data.testCases || []);
    } catch (error) {
      toastError(error);
    } finally {
      setLoadingTestCases(false);
    }
  };

  useEffect(() => {
    loadOverview();
  }, []);

  useEffect(() => {
    if (tab === "reviews") {
      loadConversations();
      return;
    }

    loadTestCases();
  }, [tab, debouncedSearch]);

  const openReviewModal = async (ticketId) => {
    try {
      const { data } = await api.get(`/ai-center/conversations/${ticketId}`);
      const conversation = data.conversation;
      const lastOperatorMessage = [...(conversation?.messages || [])]
        .reverse()
        .find((message) => message.fromMe && !message.isPrivate);

      setConversationDetail(conversation);
      setReviewForm({
        ...defaultReview,
        actualResponseExcerpt: lastOperatorMessage?.body || "",
        ...(conversation.review || {}),
        actualResponseExcerpt:
          conversation.review?.actualResponseExcerpt || lastOperatorMessage?.body || ""
      });
      setIdealSuggestions([]);
      setLoadingIdealSuggestions(false);
      setReviewModalOpen(true);
    } catch (error) {
      toastError(error);
    }
  };

  const closeReviewModal = () => {
    setConversationDetail(null);
    setReviewForm(defaultReview);
    setIdealSuggestions([]);
    setLoadingIdealSuggestions(false);
    setReviewModalOpen(false);
  };

  const handleQuickStatus = (status) => {
    const scoreMap = {
      approved: 5,
      partial: 3,
      rejected: 1,
      pending: ""
    };

    setReviewForm((prev) => ({
      ...prev,
      status,
      overallScore: scoreMap[status],
      intentScore: scoreMap[status],
      contextScore: scoreMap[status],
      toneScore: scoreMap[status],
      actionScore: scoreMap[status]
    }));
  };

  const handleGenerateIdealReply = async () => {
    if (!conversationDetail?.id) return;

    setLoadingIdealSuggestions(true);
    try {
      const { data } = await api.post(`/messages/${conversationDetail.id}/ai-suggestions`, {});
      setIdealSuggestions(data.suggestions || []);
      if (!(data.suggestions || []).length) {
        toast.info("No se generaron sugerencias para esta conversación");
      }
    } catch (error) {
      toastError(error);
    } finally {
      setLoadingIdealSuggestions(false);
    }
  };

  const handleUseSuggestionAsExpected = (text) => {
    setReviewForm((prev) => ({
      ...prev,
      expectedBehavior: text,
      status: prev.status === "pending" ? "partial" : prev.status
    }));
  };

  const handleSaveReview = async () => {
    if (!conversationDetail?.id) return;

    try {
      await api.post("/ai-center/reviews", {
        ticketId: conversationDetail.id,
        ...reviewForm
      });
      toast.success("Revisión guardada");
      closeReviewModal();
      loadOverview();
      loadConversations();
    } catch (error) {
      toastError(error);
    }
  };

  const handleCreateCaseFromReview = async () => {
    if (!conversationDetail?.review?.id && !conversationDetail?.id) return;

    try {
      let reviewId = conversationDetail?.review?.id;

      if (!reviewId) {
        const { data } = await api.post("/ai-center/reviews", {
          ticketId: conversationDetail.id,
          ...reviewForm
        });
        reviewId = data.review?.id;
      }

      const { data } = await api.post(`/ai-center/test-cases/from-review/${reviewId}`);
      toast.success("Caso de prueba creado");
      closeReviewModal();
      setTab("cases");
      loadTestCases();
      if (data?.testCase) {
        setSelectedTestCase(data.testCase);
        setTestCaseForm({
          ...defaultTestCase,
          ...data.testCase
        });
        setTestCaseModalOpen(true);
      }
    } catch (error) {
      toastError(error);
    }
  };

  const openNewTestCaseModal = () => {
    setSelectedTestCase(null);
    setTestCaseForm(defaultTestCase);
    setTestCaseModalOpen(true);
  };

  const openEditTestCaseModal = (testCase) => {
    setSelectedTestCase(testCase);
    setTestCaseForm({
      ...defaultTestCase,
      ...testCase
    });
    setTestCaseModalOpen(true);
  };

  const closeTestCaseModal = () => {
    setSelectedTestCase(null);
    setTestCaseForm(defaultTestCase);
    setTestCaseModalOpen(false);
  };

  const handleSaveTestCase = async () => {
    try {
      if (selectedTestCase?.id) {
        await api.put(`/ai-center/test-cases/${selectedTestCase.id}`, testCaseForm);
        toast.success("Caso de prueba actualizado");
      } else {
        await api.post("/ai-center/test-cases", testCaseForm);
        toast.success("Caso de prueba creado");
      }

      closeTestCaseModal();
      loadOverview();
      loadTestCases();
    } catch (error) {
      toastError(error);
    }
  };

  const handleDeleteTestCase = async (testCaseId) => {
    try {
      await api.delete(`/ai-center/test-cases/${testCaseId}`);
      toast.success("Caso de prueba eliminado");
      loadOverview();
      loadTestCases();
    } catch (error) {
      toastError(error);
    }
  };

  const handleRunTestCase = async (testCase) => {
    try {
      const { data } = await api.post(`/ai-center/test-cases/${testCase.id}/run`);
      setRunResult({
        testCase: data.testCase,
        run: data.run
      });
      toast.success("Caso ejecutado");
      loadTestCases();
    } catch (error) {
      toastError(error);
    }
  };

  if (user.profile === "user") {
    return <ForbiddenPage />;
  }

  return (
    <MainContainer>
      <Dialog open={reviewModalOpen} onClose={closeReviewModal} fullWidth maxWidth="md">
        <DialogTitle>Revisar conversación IA</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" className={classes.helperText}>
            Este flujo sirve para enseñarle al Centro IA cómo debió responder, sin activar el agente ni enviar mensajes al cliente.
          </Typography>

          {conversationDetail?.latestSummary?.summaryText ? (
            <div className={classes.summaryBox}>
              <Typography variant="subtitle2" gutterBottom>Resumen IA más reciente</Typography>
              <Typography variant="body2">{conversationDetail.latestSummary.summaryText}</Typography>
            </div>
          ) : null}

          {conversationDetail?.contact?.aiMemory ? (
            <div className={classes.summaryBox}>
              <Typography variant="subtitle2" gutterBottom>Memoria persistente del contacto</Typography>
              <Typography variant="body2">
                {JSON.stringify(conversationDetail.contact.aiMemory, null, 2)}
              </Typography>
            </div>
          ) : null}

          <Typography variant="subtitle2" gutterBottom>Últimos mensajes</Typography>
          {(conversationDetail?.messages || []).map((message) => (
            <div key={message.id} className={classes.messageBubble}>
              <div className={classes.messageMeta}>
                {message.fromMe ? "Operador / IA" : "Cliente"} · {formatDateTime(message.createdAt)}
              </div>
              <Typography variant="body2">
                {message.mediaType === "image"
                  ? "[imagen]"
                  : message.body || (message.mediaType === "audio"
                    ? "[audio]"
                    : "[sin texto]")}
              </Typography>
              {renderConversationMessageExtra(message)}
            </div>
          ))}

          <Box className={classes.quickReviewGrid}>
            <Typography variant="subtitle2" gutterBottom>Calificación rápida</Typography>
            <div className={classes.compactActions}>
              <Button
                className={classes.compactStatusBtn}
                variant={reviewForm.status === "approved" ? "contained" : "outlined"}
                color="primary"
                onClick={() => handleQuickStatus("approved")}
              >
                Aprobar
              </Button>
              <Button
                className={classes.compactStatusBtn}
                variant={reviewForm.status === "partial" ? "contained" : "outlined"}
                color="secondary"
                onClick={() => handleQuickStatus("partial")}
              >
                Parcial
              </Button>
              <Button
                className={classes.compactStatusBtn}
                variant={reviewForm.status === "rejected" ? "contained" : "outlined"}
                onClick={() => handleQuickStatus("rejected")}
              >
                Incorrecta
              </Button>
              <Button
                className={classes.compactStatusBtn}
                variant="outlined"
                startIcon={<Edit />}
                onClick={handleGenerateIdealReply}
                disabled={loadingIdealSuggestions}
              >
                {loadingIdealSuggestions ? "Generando..." : "Generar respuesta ideal"}
              </Button>
            </div>
          </Box>

          {idealSuggestions.length ? (
            <Box mb={2}>
              <Typography variant="subtitle2" gutterBottom>Respuestas ideales sugeridas</Typography>
              {idealSuggestions.map((suggestion, index) => (
                <div key={`${index}-${suggestion.text || suggestion}`} className={classes.suggestionCard}>
                  <div className={classes.suggestionHeader}>
                    <Typography variant="caption" color="textSecondary">
                      Sugerencia {index + 1}
                    </Typography>
                    <Button
                      size="small"
                      color="primary"
                      variant="outlined"
                      onClick={() => handleUseSuggestionAsExpected(suggestion.text || suggestion)}
                    >
                      Usar como ejemplo
                    </Button>
                  </div>
                  <Typography variant="body2">{suggestion.text || suggestion}</Typography>
                </div>
              ))}
            </Box>
          ) : null}

          <Grid container spacing={2} className={classes.formGrid}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Título corto"
                fullWidth
                variant="outlined"
                value={reviewForm.title}
                onChange={(e) => setReviewForm((prev) => ({ ...prev, title: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Estado</InputLabel>
                <Select
                  label="Estado"
                  value={reviewForm.status}
                  onChange={(e) => setReviewForm((prev) => ({ ...prev, status: e.target.value }))}
                >
                  <MenuItem value="pending">Pendiente</MenuItem>
                  <MenuItem value="approved">Correcta</MenuItem>
                  <MenuItem value="partial">Parcial</MenuItem>
                  <MenuItem value="rejected">Incorrecta</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Cómo debió responder"
                fullWidth
                multiline
                minRows={3}
                variant="outlined"
                value={reviewForm.expectedBehavior}
                onChange={(e) => setReviewForm((prev) => ({ ...prev, expectedBehavior: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Respuesta observada o fragmento a corregir"
                fullWidth
                multiline
                minRows={2}
                variant="outlined"
                value={reviewForm.actualResponseExcerpt}
                onChange={(e) => setReviewForm((prev) => ({ ...prev, actualResponseExcerpt: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notas cortas"
                fullWidth
                multiline
                minRows={3}
                variant="outlined"
                value={reviewForm.notes}
                onChange={(e) => setReviewForm((prev) => ({ ...prev, notes: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateCaseFromReview} color="secondary" variant="outlined">
            Convertir en caso de prueba
          </Button>
          <Button onClick={closeReviewModal} variant="outlined">Cerrar</Button>
          <Button onClick={handleSaveReview} color="primary" variant="contained">Guardar ejemplo</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={testCaseModalOpen} onClose={closeTestCaseModal} fullWidth maxWidth="md">
        <DialogTitle>{selectedTestCase ? "Editar caso de prueba" : "Nuevo caso de prueba"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Nombre"
                fullWidth
                variant="outlined"
                value={testCaseForm.name}
                onChange={(e) => setTestCaseForm((prev) => ({ ...prev, name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Estado</InputLabel>
                <Select
                  label="Estado"
                  value={testCaseForm.status}
                  onChange={(e) => setTestCaseForm((prev) => ({ ...prev, status: e.target.value }))}
                >
                  <MenuItem value="active">Activo</MenuItem>
                  <MenuItem value="archived">Archivado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Categoría"
                fullWidth
                variant="outlined"
                value={testCaseForm.category}
                onChange={(e) => setTestCaseForm((prev) => ({ ...prev, category: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Intención esperada"
                fullWidth
                variant="outlined"
                value={testCaseForm.expectedIntent}
                onChange={(e) => setTestCaseForm((prev) => ({ ...prev, expectedIntent: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Descripción"
                fullWidth
                multiline
                minRows={2}
                variant="outlined"
                value={testCaseForm.description}
                onChange={(e) => setTestCaseForm((prev) => ({ ...prev, description: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Contexto previo"
                fullWidth
                multiline
                minRows={3}
                variant="outlined"
                value={testCaseForm.priorContext}
                onChange={(e) => setTestCaseForm((prev) => ({ ...prev, priorContext: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Mensaje de entrada"
                fullWidth
                multiline
                minRows={2}
                variant="outlined"
                value={testCaseForm.inputMessage}
                onChange={(e) => setTestCaseForm((prev) => ({ ...prev, inputMessage: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Acción esperada"
                fullWidth
                variant="outlined"
                value={testCaseForm.expectedAction}
                onChange={(e) => setTestCaseForm((prev) => ({ ...prev, expectedAction: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Respuesta esperada"
                fullWidth
                variant="outlined"
                value={testCaseForm.expectedResponse}
                onChange={(e) => setTestCaseForm((prev) => ({ ...prev, expectedResponse: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTestCaseModal} variant="outlined">Cancelar</Button>
          <Button onClick={handleSaveTestCase} color="primary" variant="contained">Guardar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(runResult)} onClose={() => setRunResult(null)} fullWidth maxWidth="md">
        <DialogTitle>Resultado de prueba IA</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" gutterBottom>
            {runResult?.testCase?.name}
          </Typography>
          <Typography variant="body2" gutterBottom>
            Estado: {runResult?.run?.status || "-"} · Puntaje: {runResult?.run?.score || "-"} / 5
          </Typography>
          <Divider style={{ margin: "12px 0" }} />
          <Typography variant="subtitle2" gutterBottom>Respuesta del agente</Typography>
          <div className={classes.summaryBox}>
            <Typography variant="body2">{runResult?.run?.responseText || "-"}</Typography>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRunResult(null)} color="primary" variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <MainHeader>
        <Title>Centro IA</Title>
        <MainHeaderButtonsWrapper>
          {tab === "cases" ? (
            <Button color="primary" variant="contained" startIcon={<Add />} onClick={openNewTestCaseModal}>
              Nuevo caso
            </Button>
          ) : null}
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.toolbar}>
          <Tabs
            value={tab}
            indicatorColor="primary"
            textColor="primary"
            onChange={(_e, value) => setTab(value)}
          >
            <Tab value="reviews" label="Revisiones" />
            <Tab value="cases" label="Casos de prueba" />
          </Tabs>
          <div style={{ minWidth: 260 }}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Buscar..."
              value={searchParam}
              onChange={(e) => setSearchParam(e.target.value)}
            />
          </div>
        </div>

        <Grid container spacing={2} className={classes.cardsGrid}>
          {[
            ["Conversaciones", overview?.ticketsCount || 0],
            ["Revisiones", overview?.reviewsCount || 0],
            ["Correctas", overview?.approvedCount || 0],
            ["Parciales", overview?.partialCount || 0],
            ["Incorrectas", overview?.rejectedCount || 0],
            ["Casos de prueba", overview?.testCasesCount || 0]
          ].map(([label, value]) => (
            <Grid item xs={12} sm={6} md={2} key={label}>
              <Paper className={classes.statCard} variant="outlined">
                <div className={classes.statLabel}>{label}</div>
                <div className={classes.statValue}>{value}</div>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {tab === "reviews" ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Contacto</TableCell>
                <TableCell>Cola</TableCell>
                <TableCell>Estado ticket</TableCell>
                <TableCell>Último mensaje</TableCell>
                <TableCell>Revisión</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {conversations.map((conversation) => (
                <TableRow key={conversation.id}>
                  <TableCell>
                    <Typography variant="body2">{conversation.contact?.name || "Sin contacto"}</Typography>
                    <Typography variant="caption" display="block">
                      {conversation.contact?.number || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>{conversation.queue?.name || "Sin cola"}</TableCell>
                  <TableCell>{conversation.status}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{conversation.lastMessage || "-"}</Typography>
                    <Typography variant="caption" display="block">
                      {formatDateTime(conversation.updatedAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {conversation.review ? (
                      <Chip
                        size="small"
                        label={`${conversation.review.status} ${conversation.review.overallScore ? `· ${conversation.review.overallScore}/5` : ""}`}
                        color={statusColor(conversation.review.status)}
                        className={classes.statusChip}
                      />
                    ) : (
                      <Chip size="small" label="Sin revisar" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => openReviewModal(conversation.id)}>
                      <PlaylistAddCheck />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {loadingConversations ? <TableRowSkeleton columns={6} /> : null}
            </TableBody>
          </Table>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Categoría</TableCell>
                <TableCell>Intención</TableCell>
                <TableCell>Acción esperada</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {testCases.map((testCase) => (
                <TableRow key={testCase.id}>
                  <TableCell>
                    <Typography variant="body2">{testCase.name}</Typography>
                    <Typography variant="caption" display="block">
                      {testCase.inputMessage}
                    </Typography>
                  </TableCell>
                  <TableCell>{testCase.category || "-"}</TableCell>
                  <TableCell>{testCase.expectedIntent || "-"}</TableCell>
                  <TableCell>{testCase.expectedAction || "-"}</TableCell>
                  <TableCell>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <Chip size="small" label={testCase.status || "active"} />
                      {testCase.lastRunStatus ? (
                        <Chip
                          size="small"
                          color={testCase.lastRunStatus === "passed" ? "primary" : "default"}
                          label={`${testCase.lastRunStatus} · ${testCase.lastRunScore || "-"}/5`}
                        />
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleRunTestCase(testCase)}>
                      <PlaylistAddCheck />
                    </IconButton>
                    <IconButton size="small" onClick={() => openEditTestCaseModal(testCase)}>
                      <Edit />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteTestCase(testCase.id)}>
                      <DeleteOutline />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {loadingTestCases ? <TableRowSkeleton columns={6} /> : null}
            </TableBody>
          </Table>
        )}
      </Paper>
    </MainContainer>
  );
};

export default AiCenter;
